import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getLocalISODate } from "../bookings/user/utils";
import {
  getSessionsService,
  getAvailabilityService,
  setAvailabilityService,
  deleteAvailabilityService,
  getAvailabilityStatsService,
  getDashboardCountService,
  updateUserStatusService,
} from "../../../services/availability";
import { getCurrentUserProfileService } from "../../../services/auth";
import { getUserAvatarColor } from "../../../utils/kpiColors";
import { hasPeopleModuleAdminAccess } from "../../../utils/moduleAccess";

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 18) return "Good Afternoon";
  return "Good Evening";
};

const checkIsLeaveActiveNow = (dayAvail, sessionsList, nowTick = null) => {
  if (!dayAvail) return false;
  const s1Leave = dayAvail.s1?.status === "ON_LEAVE";
  const s2Leave = dayAvail.s2?.status === "ON_LEAVE";

  if (!s1Leave && !s2Leave) return false;
  if (s1Leave && s2Leave) return true;

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  if (s1Leave) {
    return currentMinutes < 13 * 60; // 1:00 PM cutoff for morning half
  }
  if (s2Leave) {
    return currentMinutes >= 13 * 60; // 1:00 PM start for afternoon half
  }

  return false;
};

const DashboardHeader = ({ firstName, userName, todayBooking, isSuperAdmin }) => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);

  const [hasIdlePermission, setHasIdlePermission] = useState(() =>
    hasPeopleModuleAdminAccess(null, authState)
  );

  useEffect(() => {
    setHasIdlePermission(hasPeopleModuleAdminAccess(null, authState));
  }, [authState]);

  const todayMoment = useMemo(() => moment(), []);
  const todayISO = useMemo(() => getLocalISODate(new Date()), []);
  const formattedToday = useMemo(
    () => todayMoment.format("dddd · MMM D, YYYY"),
    [todayMoment]
  );

  const [sessions, setSessions] = useState([]);
  const [dayAvailability, setDayAvailability] = useState({ s1: null, s2: null });
  const [stats, setStats] = useState({ working: 0, total: 0, on_leave: 0, idle: 0 });
  const [loadingAction, setLoadingAction] = useState(false);

  // Online / Away status feature state
  const [userOnlineStatus, setUserOnlineStatus] = useState("online");

  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNowTick(Date.now());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const isLeaveActiveNow = useMemo(() => {
    return checkIsLeaveActiveNow(dayAvailability, sessions, nowTick);
  }, [dayAvailability, sessions, nowTick]);

  const wasLeaveActiveRef = useRef(isLeaveActiveNow);

  // Sync user status with backend when leave status changes
  useEffect(() => {
    if (isLeaveActiveNow) {
      if (userOnlineStatus !== "offline") {
        setUserOnlineStatus("offline");
        updateUserStatusService("offline").catch((err) =>
          console.error("Failed to update status to offline on leave:", err)
        );
      }
    } else if (wasLeaveActiveRef.current && !isLeaveActiveNow) {
      setUserOnlineStatus("online");
      updateUserStatusService("online").catch((err) =>
        console.error("Failed to restore status to online:", err)
      );
    }
    wasLeaveActiveRef.current = isLeaveActiveNow;
  }, [isLeaveActiveNow, userOnlineStatus]);

  const handleToggleOnlineStatus = async (targetStatus) => {
    if (loadingAction || isLeaveActiveNow) return;
    const nextStatus = targetStatus || (userOnlineStatus === "online" ? "offline" : "online");
    if (userOnlineStatus === nextStatus) return;
    setUserOnlineStatus(nextStatus);

    try {
      await updateUserStatusService(nextStatus);
    } catch (err) {
      console.error("Failed to update status on server:", err);
    }
  };

  // Modal State for Mark Leave / Idle
  const [activeModal, setActiveModal] = useState(null); // 'leave' | 'idle' | null
  const [selectedDuration, setSelectedDuration] = useState("FULL_DAY"); // 'FULL_DAY' | 'SESSION_MORNING' | 'SESSION_AFTERNOON'
  const [reasonText, setReasonText] = useState("");

  // Modal State for Member Availability Lists (WORKING, ON_LEAVE, IDLE)
  const [listModal, setListModal] = useState({
    isOpen: false,
    type: "", // 'WORKING' | 'ON_LEAVE' | 'IDLE'
    title: "",
    users: [],
    loading: false,
    searchQuery: "",
  });

  const handleOpenListModal = async (type) => {
    const titles = {
      WORKING: "Working Members",
      ON_LEAVE: "Members On Leave",
      IDLE: "Idle Members",
    };

    setListModal({
      isOpen: true,
      type,
      title: titles[type] || type,
      users: [],
      loading: true,
      searchQuery: "",
    });

    try {
      let userList = [];
      if (type === "WORKING") {
        const res = await getAvailabilityService({ limit: 100 });
        const rawData = res?.data || (Array.isArray(res) ? res : []);
        userList = Array.isArray(rawData) ? rawData : [];
      } else if (type === "ON_LEAVE") {
        const res = await getAvailabilityService({ status: "ON_LEAVE", limit: 100 });
        const rawData = res?.data || (Array.isArray(res) ? res : []);
        userList = Array.isArray(rawData) ? rawData : [];
      } else if (type === "IDLE") {
        const res = await getAvailabilityService({ status: "IDLE", limit: 100 });
        const rawData = res?.data || (Array.isArray(res) ? res : []);
        userList = Array.isArray(rawData) ? rawData : [];
      }

      setListModal((prev) => ({
        ...prev,
        loading: false,
        users: userList,
      }));
    } catch (err) {
      console.error(`Error loading ${type} members list:`, err);
      setListModal((prev) => ({
        ...prev,
        loading: false,
        users: [],
      }));
    }
  };

  // Track explicitly cancelled availability keys (IDs or date strings)
  const [cancelledKeys, setCancelledKeys] = useState([]);

  const addCancelledKey = (key) => {
    if (!key) return;
    setCancelledKeys((prev) => {
      if (prev.includes(key)) return prev;
      return [...prev, key];
    });
  };

  const removeCancelledKey = (key) => {
    if (!key) return;
    setCancelledKeys((prev) => prev.filter((k) => k !== key && !k.startsWith(key)));
  };

  const parseSessionsToDayAvailability = (sessionsArr, profileData, cancelledList = []) => {
    let s1 = null;
    let s2 = null;

    const rawArr = Array.isArray(sessionsArr) ? sessionsArr : [];
    const validArr = rawArr.filter((item) => {
      const idKey = item._id || item.id || item.session_id;
      return !cancelledList.includes(idKey);
    });

    validArr.forEach((item) => {
      const st = item.status || item.availability_status;
      if (st !== "ON_LEAVE" && st !== "IDLE") return;

      const dur = (item.duration || "").toUpperCase();
      const sObj = item.session_id || item.session;
      const sName = (
        (typeof sObj === "object" ? sObj?.session_name || sObj?.name : null) ||
        item.session_name ||
        item.session_type ||
        ""
      ).toLowerCase();
      const sid = String(typeof sObj === "string" ? sObj : sObj?._id || sObj?.id || "").toLowerCase();

      const isS1 =
        sName.includes("morning") ||
        sName.includes("first") ||
        sName.includes("1") ||
        sName === "session1" ||
        sid.includes("morning") ||
        sid.includes("session1") ||
        sid === "1";

      const isS2 =
        sName.includes("afternoon") ||
        sName.includes("second") ||
        sName.includes("2") ||
        sName === "session2" ||
        sid.includes("afternoon") ||
        sid.includes("session2") ||
        sid === "2";

      if (dur === "FULL_DAY" || (!isS1 && !isS2)) {
        if (!s1) s1 = { status: st, record: item, session_name: "Session1" };
        if (!s2) s2 = { status: st, record: item, session_name: "Session2" };
      } else {
        if (isS1) s1 = { status: st, record: item, session_name: "Session1" };
        if (isS2) s2 = { status: st, record: item, session_name: "Session2" };
      }
    });

    if (!s1 && !s2 && profileData) {
      const st = profileData.availability_status || (profileData.status && profileData.status !== "online" && profileData.status !== "offline" ? profileData.status : null);
      if (st === "ON_LEAVE" || st === "IDLE") {
        const dur = (profileData.duration || "FULL_DAY").toUpperCase();
        const sName = (profileData.session_name || "").toLowerCase();
        const sid = String(profileData.session_id || "").toLowerCase();

        const isS1 = sName.includes("1") || sName.includes("morning") || sName.includes("first") || sid.includes("1");
        const isS2 = sName.includes("2") || sName.includes("afternoon") || sName.includes("second") || sid.includes("2");

        if (dur === "FULL_DAY" || (!isS1 && !isS2)) {
          s1 = { status: st, record: profileData, session_name: "Session1" };
          s2 = { status: st, record: profileData, session_name: "Session2" };
        } else if (isS1) {
          s1 = { status: st, record: profileData, session_name: "Session1" };
        } else if (isS2) {
          s2 = { status: st, record: profileData, session_name: "Session2" };
        }
      }
    }

    return { s1, s2 };
  };

  // Load Sessions, Today Availability (via current-user-profile), Dashboard Counts, and Member Stats on Mount
  const loadHeaderData = async (overrideActiveRecord = null) => {
    try {
      const [sessData, profileRes, countData, statsData] = await Promise.all([
        getSessionsService(),
        getCurrentUserProfileService(),
        getDashboardCountService({ date: todayISO }),
        getAvailabilityStatsService({ start_date: todayISO, end_date: todayISO }),
      ]);

      if (Array.isArray(sessData) && sessData.length > 0) {
        setSessions(sessData);
      }

      // Process current user profile response from /current-user-profile API
      const profileData = profileRes?.data?.data || profileRes?.data || profileRes || {};

      // Check for People Module Admin permission to show/hide idle section
      const hasPerm = hasPeopleModuleAdminAccess(profileData, authState);
      setHasIdlePermission(hasPerm);

      // 1. Map current online / offline status (e.g. current_status: "online" / "offline")
      const rawCurrentStatus = profileData.current_status || profileData.user_status || (typeof profileData.status === "string" && (profileData.status.toLowerCase() === "online" || profileData.status.toLowerCase() === "offline") ? profileData.status : null);
      
      if (rawCurrentStatus) {
        const formattedStatus = rawCurrentStatus.toLowerCase();
        setUserOnlineStatus(formattedStatus);
      }

      // 2. Parse session_wise_availability or todays_availability / leave fields
      if (overrideActiveRecord) {
        if (overrideActiveRecord.s1 !== undefined || overrideActiveRecord.s2 !== undefined) {
          setDayAvailability(overrideActiveRecord);
        } else {
          const parsed = parseSessionsToDayAvailability([overrideActiveRecord], overrideActiveRecord, cancelledKeys);
          setDayAvailability(parsed);
        }
      } else {
        const targetDateStr = todayISO.includes("T") ? todayISO.split("T")[0] : todayISO;
        const sessionsArr = profileData.session_wise_availability || profileData.todays_availability || profileData.availability || profileData.active_availability || profileData.leave;

        const parsed = parseSessionsToDayAvailability(sessionsArr, profileData, cancelledKeys);
        setDayAvailability(parsed);
      }

      // Parse counts from getDashboardCountService API response: {"success":true,"data":{"working_users":75,"idle_users":1,"on_leave_users":2}}
      const parseCountObj = (raw) => {
        if (!raw || typeof raw !== "object") return null;
        const res = raw.data?.data || raw.data || raw.count || raw;

        const getNum = (v) => {
          if (typeof v === "number") return v;
          if (typeof v === "string" && !isNaN(Number(v))) return Number(v);
          if (v && typeof v === "object") {
            if (typeof v.count === "number") return v.count;
            if (typeof v.total === "number") return v.total;
            if (typeof v.value === "number") return v.value;
            if (Array.isArray(v)) return v.length;
          }
          return null;
        };

        const w =
          getNum(res.working_users) ??
          getNum(res.working) ??
          getNum(res.working_members) ??
          getNum(res.workingCount) ??
          getNum(res.working_count);

        const i =
          getNum(res.idle_users) ??
          getNum(res.idle) ??
          getNum(res.idle_members) ??
          getNum(res.idleCount) ??
          getNum(res.idle_count);

        const l =
          getNum(res.on_leave_users) ??
          getNum(res.on_leave) ??
          getNum(res.onLeave) ??
          getNum(res.leave) ??
          getNum(res.leaveCount) ??
          getNum(res.on_leave_members);

        const t =
          getNum(res.total_users) ??
          getNum(res.total) ??
          getNum(res.total_members) ??
          getNum(res.totalCount);

        if (w !== null || l !== null || i !== null || t !== null) {
          const wVal = w ?? 0;
          const lVal = l ?? 0;
          const iVal = i ?? 0;
          const tVal = t ?? (wVal + lVal + iVal);
          return { working: wVal, on_leave: lVal, idle: iVal, total: tVal };
        }
        return null;
      };

      const parsedCounts = parseCountObj(countData) || parseCountObj(statsData);

      if (parsedCounts) {
        setStats(parsedCounts);
      } else {
        const c = countData || statsData || {};
        const workingVal = c.working_users ?? c.working ?? c.workingCount ?? c.working_members ?? 0;
        const leaveVal = c.on_leave_users ?? c.on_leave ?? c.onLeave ?? c.leaveCount ?? 0;
        const idleVal = c.idle_users ?? c.idle ?? c.idleCount ?? c.idle_members ?? 0;
        const totalVal = c.total_users ?? c.total ?? c.totalCount ?? (workingVal + leaveVal + idleVal);

        setStats({
          working: workingVal,
          total: totalVal,
          on_leave: leaveVal,
          idle: idleVal,
        });
      }
    } catch (err) {
      console.error("Error loading header availability data:", err);
    }
  };

  useEffect(() => {
    loadHeaderData();
  }, [todayISO, cancelledKeys]);

  // Derived current status
  const isLeaveActive = dayAvailability.s1?.status === "ON_LEAVE" || dayAvailability.s2?.status === "ON_LEAVE";
  const isIdleActive = dayAvailability.s1?.status === "IDLE" || dayAvailability.s2?.status === "IDLE";

  // Morning & Afternoon sessions helper
  const morningSession = useMemo(
    () =>
      sessions.find((s) => {
        const name = (s.session_name || s.name || "").toLowerCase();
        return name.includes("morning") || name.includes("first") || name.includes("1") || name === "session1";
      }) || sessions[0],
    [sessions]
  );
  const afternoonSession = useMemo(
    () =>
      sessions.find((s) => {
        const name = (s.session_name || s.name || "").toLowerCase();
        return name.includes("afternoon") || name.includes("second") || name.includes("2") || name === "session2";
      }) || sessions[1] || sessions[0],
    [sessions]
  );

  // Status Badges List
  const statusBadgesList = useMemo(() => {
    const badges = [];
    const { s1, s2 } = dayAvailability;

    if (s1 && s2) {
      if (s1.status === "ON_LEAVE" && s2.status === "ON_LEAVE") {
        badges.push({
          key: "leave_fullday",
          label: "On Leave · Full day",
          color: "bg-rose-500/10 text-rose-400 border-rose-500/20",
          dotColor: "bg-rose-500",
        });
      } else if (s1.status === "IDLE" && s2.status === "IDLE") {
        badges.push({
          key: "idle_fullday",
          label: "Idle · Full day",
          color: "bg-amber-500/10 text-amber-300 border-amber-500/20",
          dotColor: "bg-amber-400",
        });
      } else {
        if (s1.status === "ON_LEAVE") {
          badges.push({
            key: "leave_s1",
            label: "Leave - First Half",
            color: "bg-rose-500/10 text-rose-400 border-rose-500/20",
            dotColor: "bg-rose-500",
          });
        } else if (s1.status === "IDLE") {
          badges.push({
            key: "idle_s1",
            label: "Idle - First Half",
            color: "bg-amber-500/10 text-amber-300 border-amber-500/20",
            dotColor: "bg-amber-400",
          });
        }

        if (s2.status === "ON_LEAVE") {
          badges.push({
            key: "leave_s2",
            label: "Leave - Second Half",
            color: "bg-rose-500/10 text-rose-400 border-rose-500/20",
            dotColor: "bg-rose-500",
          });
        } else if (s2.status === "IDLE") {
          badges.push({
            key: "idle_s2",
            label: "Idle - Second Half",
            color: "bg-amber-500/10 text-amber-300 border-amber-500/20",
            dotColor: "bg-amber-400",
          });
        }
      }
    } else {
      if (s1) {
        const isL = s1.status === "ON_LEAVE";
        badges.push({
          key: isL ? "leave_s1" : "idle_s1",
          label: isL ? "Leave - First Half" : "Idle - First Half",
          color: isL ? "bg-rose-500/10 text-rose-400 border-rose-500/20" : "bg-amber-500/10 text-amber-300 border-amber-500/20",
          dotColor: isL ? "bg-rose-500" : "bg-amber-400",
        });
      }
      if (s2) {
        const isL = s2.status === "ON_LEAVE";
        badges.push({
          key: isL ? "leave_s2" : "idle_s2",
          label: isL ? "Leave - Second Half" : "Idle - Second Half",
          color: isL ? "bg-rose-500/10 text-rose-400 border-rose-500/20" : "bg-amber-500/10 text-amber-300 border-amber-500/20",
          dotColor: isL ? "bg-rose-500" : "bg-amber-400",
        });
      }
    }

    return badges;
  }, [dayAvailability]);

  // Open Modal Handler
  const handleOpenModal = (type) => {
    setActiveModal(type);
    setSelectedDuration("FULL_DAY");
    setReasonText("");
  };

  // Submit Leave / Idle
  const handleConfirmSubmit = async () => {
    if (!activeModal || loadingAction) return;
    setLoadingAction(true);

    try {
      const targetStatus = activeModal === "leave" ? "ON_LEAVE" : "IDLE";
      let payload = {
        date: todayISO,
        status: targetStatus,
        reason: reasonText.trim() || (activeModal === "leave" ? "Leave" : "Idle"),
      };

      let selectedSessionObj = null;
      if (selectedDuration === "FULL_DAY") {
        payload.duration = "FULL_DAY";
      } else if (selectedDuration === "SESSION_MORNING") {
        payload.duration = "SESSION";
        selectedSessionObj = morningSession || { _id: "session_morning", session_name: "Session1" };
        payload.session_id = selectedSessionObj?._id || selectedSessionObj?.id || "session_morning";
      } else if (selectedDuration === "SESSION_AFTERNOON") {
        payload.duration = "SESSION";
        selectedSessionObj = afternoonSession || { _id: "session_afternoon", session_name: "Session2" };
        payload.session_id = selectedSessionObj?._id || selectedSessionObj?.id || "session_afternoon";
      }

      setCancelledKeys([]);

      const res = await setAvailabilityService(payload);
      const createdRecord = res?.data || res || payload;

      let nextS1 = dayAvailability.s1;
      let nextS2 = dayAvailability.s2;

      const newRec = {
        ...payload,
        ...(typeof createdRecord === "object" ? createdRecord : {}),
        status: targetStatus,
        duration: payload.duration,
        session_id: payload.session_id || selectedSessionObj,
        session: selectedSessionObj,
        date: todayISO,
      };

      if (selectedDuration === "FULL_DAY") {
        nextS1 = { status: targetStatus, record: newRec, session_name: "Full day" };
        nextS2 = { status: targetStatus, record: newRec, session_name: "Full day" };
      } else if (selectedDuration === "SESSION_MORNING") {
        nextS1 = { status: targetStatus, record: newRec, session_name: "Session1" };
      } else if (selectedDuration === "SESSION_AFTERNOON") {
        nextS2 = { status: targetStatus, record: newRec, session_name: "Session2" };
      }

      const newDayState = { s1: nextS1, s2: nextS2 };
      setDayAvailability(newDayState);
      setActiveModal(null);

      await loadHeaderData(newDayState);
    } catch (err) {
      console.error("Failed to set availability:", err);
      alert(err?.response?.data?.message || err?.message || "Failed to update availability status");
    } finally {
      setLoadingAction(false);
    }
  };

  // Cancel / Delete Availability (DELETE /api/availability)
  const handleCancelAvailability = async (targetType) => {
    if (loadingAction) return;
    setLoadingAction(true);

    const targetStatus = targetType || (dayAvailability.s1?.status || dayAvailability.s2?.status);

    const recordsToDelete = [];
    if (dayAvailability.s1?.status === targetStatus && dayAvailability.s1.record) {
      recordsToDelete.push(dayAvailability.s1.record);
    }
    if (dayAvailability.s2?.status === targetStatus && dayAvailability.s2.record) {
      const rec2 = dayAvailability.s2.record;
      if (!recordsToDelete.some(r => (r._id || r.id || r.session_id) === (rec2._id || rec2.id || rec2.session_id))) {
        recordsToDelete.push(rec2);
      }
    }

    const nextS1 = dayAvailability.s1?.status === targetStatus ? null : dayAvailability.s1;
    const nextS2 = dayAvailability.s2?.status === targetStatus ? null : dayAvailability.s2;
    const nextDayState = { s1: nextS1, s2: nextS2 };
    setDayAvailability(nextDayState);

    try {
      for (const rec of recordsToDelete) {
        const cancelledId = rec._id || rec.id || rec.session_id;
        if (cancelledId) addCancelledKey(cancelledId);

        const isSession =
          rec.duration === "SESSION" ||
          (Boolean(rec.session_id) && rec.duration !== "FULL_DAY");

        const sid =
          typeof rec.session_id === "object"
            ? rec.session_id?._id
            : rec.session_id;

        const payload = {
          date: todayISO,
          duration: isSession ? "SESSION" : "FULL_DAY",
          id: cancelledId,
          _id: cancelledId,
        };

        if (isSession && sid) {
          payload.session_id = sid;
        }

        await deleteAvailabilityService(payload);
      }

      await loadHeaderData();
    } catch (err) {
      console.error("Failed to cancel availability:", err);
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <div className="w-full">
      {/* 1. Main Dark Hero Card Header */}
      <div className="bg-[#111111] text-white rounded-[24px] p-5 sm:p-6 mb-6 shadow-xl relative overflow-hidden border border-white/5">
        <div className="flex flex-col gap-5">
          {/* Row 1: GREETING + STATUS BADGE + ACTION BUTTONS */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Left: Greeting & Status */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                {formattedToday}
              </p>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 text-white">
                {getGreeting()}, {firstName} 
              </h1>

              {/* Status Badges Row (Online / Offline Control [Hidden during active leave] + Separate Leave / Idle Badge) */}
              <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
                {/* Online / Offline Status Segmented Switch Control (Hidden when leave is active right now) */}
                {!isLeaveActiveNow && (
                  <div className="inline-flex items-center p-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-md shadow-inner">
                    {/* Online Tab */}
                    <button
                      type="button"
                      onClick={() => handleToggleOnlineStatus("online")}
                      disabled={loadingAction}
                      className={`relative px-3 py-1 rounded-full text-xs font-bold transition-all duration-200 flex items-center gap-1.5 active:scale-95 ${
                        userOnlineStatus === "online"
                          ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-xs"
                          : "text-slate-400 hover:text-white border border-transparent"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${userOnlineStatus === "online" ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
                      <span>Online</span>
                    </button>

                    {/* Offline Tab */}
                    <button
                      type="button"
                      onClick={() => handleToggleOnlineStatus("offline")}
                      disabled={loadingAction}
                      className={`relative px-3 py-1 rounded-full text-xs font-bold transition-all duration-200 flex items-center gap-1.5 active:scale-95 ${
                        userOnlineStatus === "offline"
                          ? "bg-amber-500/20 text-amber-300 border border-amber-500/30 shadow-xs"
                          : "text-slate-400 hover:text-white border border-transparent"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${userOnlineStatus === "offline" ? "bg-amber-400 animate-pulse" : "bg-slate-500"}`} />
                      <span>Offline</span>
                    </button>
                  </div>
                )}

                {/* Separate Leave or Idle Badges (Displayed whenever Leave/Idle is active for today) */}
                {statusBadgesList.map((badge) => (
                  <span
                    key={badge.key}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border transition-all ${badge.color}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${badge.dotColor} animate-pulse`} />
                    <span>{badge.label}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Quick Action Buttons (Mark Leave / Mark Idle / Cancel) */}
            <div className="flex items-center gap-2 self-start sm:self-center">
              {/* Leave Button / Cancel Leave */}
              {isLeaveActive ? (
                <button
                  onClick={() => handleCancelAvailability("ON_LEAVE")}
                  disabled={loadingAction}
                  className="py-2 px-3.5 bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-500/30 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all active:scale-95"
                >
                  <span>Cancel Leave</span>
                </button>
              ) : (
                <button
                  onClick={() => handleOpenModal("leave")}
                  className="py-2 px-3.5 bg-white/10 hover:bg-white/15 text-white border border-white/10 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all active:scale-95"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                  <span>Mark as Leave</span>
                </button>
              )}

              {/* Idle Button / Cancel Idle */}
              {isIdleActive ? (
                <button
                  onClick={() => handleCancelAvailability("IDLE")}
                  disabled={loadingAction}
                  className="py-2 px-3.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all active:scale-95"
                >
                  <span>Cancel Idle</span>
                </button>
              ) : (
                <button
                  onClick={() => handleOpenModal("idle")}
                  className="py-2 px-3.5 bg-white/10 hover:bg-white/15 text-white border border-white/10 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all active:scale-95"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6" />
                  </svg>
                  <span>Mark as Idle</span>
                </button>
              )}
            </div>
          </div>

          {/* Row 2: WORKSPACE (Today's Booking summary + Book a seat button) */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center text-sm text-white shrink-0 font-bold">
                {todayBooking ? (
                  todayBooking.seatLabel
                ) : (
                  <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 18v-4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4M6 12V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v5M4 18h16M7 18v3M17 18v3" />
                  </svg>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Today's Booking
                </span>
                <span className="text-sm font-semibold text-white truncate max-w-[200px] sm:max-w-[300px]">
                  {todayBooking ? `${todayBooking.seatType} · ${todayBooking.sectionName}` : "No seat reserved for today"}
                </span>
              </div>
            </div>

            {!isSuperAdmin && (
              <button
                onClick={() => navigate("/bookings")}
                className="py-2 px-4 bg-white text-slate-900 hover:bg-slate-100 transition-all active:scale-95 font-bold text-xs rounded-xl shadow-sm whitespace-nowrap shrink-0 flex items-center gap-1.5"
              >
                <span>+</span>
                <span>Book a seat</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 2. Availability Statistics Cards Row (Clickable - Working and Leave shown to all, Idle shown based on permission) */}
      <div className={`grid ${hasIdlePermission ? "grid-cols-3" : "grid-cols-2"} gap-3 sm:gap-4 mb-6`}>
        {/* WORKING */}
        <div 
          onClick={() => handleOpenListModal("WORKING")}
          className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-xs hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer group"
          title="Click to view working members"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0 group-hover:scale-125 transition-transform" />
              <span className="text-[11px] sm:text-xs font-bold text-slate-500 tracking-wider uppercase truncate group-hover:text-slate-900 transition-colors">
                WORKING
              </span>
            </div>
            <span className="text-slate-300 text-xs group-hover:text-emerald-600 transition-colors font-bold">➔</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-slate-900 mt-1">
            {stats.working}
          </div>
        </div>

        {/* ON LEAVE */}
        <div 
          onClick={() => handleOpenListModal("ON_LEAVE")}
          className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-xs hover:shadow-md hover:border-rose-300 transition-all cursor-pointer group"
          title="Click to view members on leave"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0 group-hover:scale-125 transition-transform" />
              <span className="text-[11px] sm:text-xs font-bold text-slate-500 tracking-wider uppercase truncate group-hover:text-slate-900 transition-colors">
                ON LEAVE
              </span>
            </div>
            <span className="text-slate-300 text-xs group-hover:text-rose-600 transition-colors font-bold">➔</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-slate-900 mt-1">
            {stats.on_leave ?? 0}
          </div>
        </div>

        {/* IDLE (Only rendered if user has People Module Admin permission) */}
        {hasIdlePermission && (
          <div 
            onClick={() => handleOpenListModal("IDLE")}
            className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-xs hover:shadow-md hover:border-amber-300 transition-all cursor-pointer group"
            title="Click to view idle members"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0 group-hover:scale-125 transition-transform" />
                <span className="text-[11px] sm:text-xs font-bold text-slate-500 tracking-wider uppercase truncate group-hover:text-slate-900 transition-colors">
                  IDLE
                </span>
              </div>
              <span className="text-slate-300 text-xs group-hover:text-amber-600 transition-colors font-bold">➔</span>
            </div>
            <div className="text-xl sm:text-2xl font-black text-slate-900 mt-1">
              {stats.idle ?? 0}
            </div>
          </div>
        )}
      </div>

      {/* 3. Mark Leave / Mark Idle Modal Bottom Sheet */}
      {activeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 transition-opacity animate-fadeIn">
          <div className="bg-white w-full sm:max-w-md rounded-t-[28px] sm:rounded-[28px] p-6 shadow-2xl z-[9999] transform transition-all animate-slideUp">
            {/* Top Handle bar for mobile sheet styling */}
            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-4 sm:hidden" />

            {/* Header Title */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-xl font-bold text-slate-900 capitalize">
                  Mark {activeModal}
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5">
                  {formattedToday}
                </p>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center text-xs font-bold transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Duration Options List */}
            <div className="space-y-3 mb-5">
              {/* Full Day */}
              <div
                onClick={() => setSelectedDuration("FULL_DAY")}
                className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                  selectedDuration === "FULL_DAY"
                    ? "border-slate-900 bg-slate-50/80 shadow-xs"
                    : "border-slate-100 bg-white hover:border-slate-200"
                }`}
              >
                <div>
                  <div className="text-sm font-bold text-slate-900">Full Day</div>
                  <div className="text-xs text-slate-500 font-medium mt-0.5">
                    {activeModal === "leave" ? "Out of office for the whole day" : "Idle for the whole day"}
                  </div>
                </div>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    selectedDuration === "FULL_DAY"
                      ? "bg-slate-900 text-white"
                      : "border-2 border-slate-300"
                  }`}
                >
                  {selectedDuration === "FULL_DAY" && (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </div>

              {/* First Half (Morning Session) */}
              <div
                onClick={() => setSelectedDuration("SESSION_MORNING")}
                className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                  selectedDuration === "SESSION_MORNING"
                    ? "border-slate-900 bg-slate-50/80 shadow-xs"
                    : "border-slate-100 bg-white hover:border-slate-200"
                }`}
              >
                <div>
                  <div className="text-sm font-bold text-slate-900">First Half</div>
                  <div className="text-xs text-slate-500 font-medium mt-0.5">
                    Morning · 09:00 – 13:00
                  </div>
                </div>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    selectedDuration === "SESSION_MORNING"
                      ? "bg-slate-900 text-white"
                      : "border-2 border-slate-300"
                  }`}
                >
                  {selectedDuration === "SESSION_MORNING" && (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Second Half (Afternoon Session) */}
              <div
                onClick={() => setSelectedDuration("SESSION_AFTERNOON")}
                className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                  selectedDuration === "SESSION_AFTERNOON"
                    ? "border-slate-900 bg-slate-50/80 shadow-xs"
                    : "border-slate-100 bg-white hover:border-slate-200"
                }`}
              >
                <div>
                  <div className="text-sm font-bold text-slate-900">Second Half</div>
                  <div className="text-xs text-slate-500 font-medium mt-0.5">
                    Afternoon · 14:00 – 18:00
                  </div>
                </div>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    selectedDuration === "SESSION_AFTERNOON"
                      ? "bg-slate-900 text-white"
                      : "border-2 border-slate-300"
                  }`}
                >
                  {selectedDuration === "SESSION_AFTERNOON" && (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </div>
            </div>



            {/* Confirm Button */}
            <button
              onClick={handleConfirmSubmit}
              disabled={loadingAction}
              className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold text-sm hover:bg-slate-800 transition-all flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-50"
            >
              {loadingAction && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>Confirm {activeModal}</span>
            </button>
          </div>
        </div>
      )}

      {/* 4. Member Availability List Modal (WORKING, ON_LEAVE, IDLE) */}
      {listModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[9999] flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl z-[9999] flex flex-col max-h-[85vh] animate-scaleUp">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${
                    listModal.type === 'WORKING' ? 'bg-emerald-500' : (listModal.type === 'ON_LEAVE' ? 'bg-rose-500' : 'bg-amber-400')
                  }`} />
                  <h3 className="text-lg font-bold text-slate-900">
                    {listModal.title}
                  </h3>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold font-mono">
                    {listModal.users.length}
                  </span>
                </div>
                <p className="text-xs text-slate-400 font-medium mt-1">
                  {formattedToday}
                </p>
              </div>
              <button
                onClick={() => setListModal((prev) => ({ ...prev, isOpen: false }))}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center text-xs font-bold transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Search Filter Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search member by name or email..."
                value={listModal.searchQuery}
                onChange={(e) => setListModal((prev) => ({ ...prev, searchQuery: e.target.value }))}
                className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
              />
            </div>

            {/* User List Body */}
            <div className="flex-1 overflow-y-auto pr-1 space-y-2.5 scrollbar-thin">
              {listModal.loading ? (
                <div className="py-12 text-center text-xs text-slate-400 font-medium flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
                  Loading members...
                </div>
              ) : listModal.users.length === 0 ? (
                <div className="py-12 text-center text-xs text-slate-400 font-medium">
                  No members found in {listModal.title.toLowerCase()}.
                </div>
              ) : (
                listModal.users
                  .filter((u) => {
                    if (!listModal.searchQuery.trim()) return true;
                    const q = listModal.searchQuery.toLowerCase();
                    const rawUName = u.user?.full_name || u.full_name || (typeof u.user?.name === 'object' ? u.user?.name?.name : u.user?.name) || (typeof u.name === 'object' ? u.name?.name : u.name) || `${u.first_name || ''} ${u.last_name || ''}`;
                    const name = (typeof rawUName === 'string' ? rawUName : '').toLowerCase();
                    const email = (u.user?.email || u.email || '').toLowerCase();
                    return name.includes(q) || email.includes(q);
                  })
                  .map((item, idx) => {
                    const uObj = item.user || item;
                    const firstName = typeof uObj.first_name === 'string' ? uObj.first_name : "";
                    const lastName = typeof uObj.last_name === 'string' ? uObj.last_name : "";
                    const combinedName = `${firstName} ${lastName}`.trim();
                    const rawNameVal = typeof uObj.name === 'object' ? (uObj.name?.name || uObj.name?.full_name) : uObj.name;
                    const nameStr = (typeof uObj.full_name === 'string' && uObj.full_name.trim())
                      ? uObj.full_name
                      : (combinedName || (typeof rawNameVal === 'string' ? rawNameVal : null) || (typeof uObj.email === 'string' ? uObj.email : '') || "Member");
                    const emailStr = typeof uObj.email === 'string' ? uObj.email : (typeof item.email === 'string' ? item.email : "");
                    
                    const getDesignationStr = (obj, itemObj) => {
                      const d = obj?.designation || itemObj?.designation || obj?.designation_details;
                      if (d) {
                        if (typeof d === "string") return d;
                        if (typeof d === "object") return d.name || d.designation_name || d.title || "";
                      }
                      const r = obj?.role || itemObj?.role;
                      if (r) {
                        if (typeof r === "string") return r;
                        if (typeof r === "object") return r.name || r.role_name || r.title || "";
                      }
                      return "";
                    };
                    const designation = getDesignationStr(uObj, item);

                    const parts = nameStr.trim().split(" ").filter(Boolean);
                    const initials = parts.length >= 2
                      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
                      : (nameStr.slice(0, 2) || "TM").toUpperCase();

                    const avatarBg = getUserAvatarColor(emailStr || nameStr, idx);

                    return (
                      <div key={item._id || item.id || idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-50/70 border border-slate-100 hover:bg-slate-100/80 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-9 h-9 rounded-full ${avatarBg} text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-2xs`}>
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-slate-900 truncate">{nameStr}</div>
                            {designation && <div className="text-[10px] text-slate-500 font-medium truncate">{designation}</div>}
                          </div>
                        </div>

                        {/* Status Badges */}
                        <div className="shrink-0 ml-3 text-right">
                          {listModal.type === "WORKING" ? (
                            (() => {
                              const rawSt = uObj.current_status || item.current_status || item.online_status || item.status;
                              const onlineSt = (typeof rawSt === 'object' ? (rawSt?.name || rawSt?.status || 'offline') : (rawSt || "offline")).toLowerCase();
                              if (onlineSt === "online") {
                                return (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                    Online
                                  </span>
                                );
                              } else if (onlineSt === "away") {
                                return (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                                    Away
                                  </span>
                                );
                              } else {
                                return (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                                    Offline
                                  </span>
                                );
                              }
                            })()
                          ) : (
                            (() => {
                              const getSessionInfo = (rec) => {
                                if (!rec) return { periodLabel: "Full Day", badgeColor: "bg-indigo-50 text-indigo-700 border-indigo-200" };

                                let rawSessList = [];
                                if (Array.isArray(rec.sessions) && rec.sessions.length > 0) {
                                  rawSessList = rec.sessions;
                                } else if (rec.session || rec.session_id || rec.session_details) {
                                  rawSessList = [rec.session || rec.session_id || rec.session_details];
                                }

                                const sessionNames = [];
                                const sessionIds = [];

                                rawSessList.forEach((sItem) => {
                                  if (!sItem) return;
                                  if (typeof sItem === "object") {
                                    const masterSess = sItem.session || sItem.session_details || sItem.session_id || sItem;
                                    let name = "";
                                    let id = "";

                                    if (typeof masterSess === "object" && masterSess !== null) {
                                      name = masterSess.session_name || masterSess.name || masterSess.title || masterSess.session_type || "";
                                      id = String(masterSess._id || masterSess.id || "").toLowerCase();
                                    } else if (typeof masterSess === "string") {
                                      name = masterSess;
                                      id = masterSess.toLowerCase();
                                    }

                                    if (!name) name = sItem.session_name || sItem.name || sItem.session_type || "";
                                    if (!id) id = String(sItem.session_id || sItem._id || sItem.id || "").toLowerCase();

                                    if (name) sessionNames.push(name);
                                    if (id) sessionIds.push(id);
                                  } else if (typeof sItem === "string") {
                                    sessionNames.push(sItem);
                                    sessionIds.push(sItem.toLowerCase());
                                  }
                                });

                                if (rec.session_name) sessionNames.push(rec.session_name);
                                if (rec.session_type) sessionNames.push(rec.session_type);

                                const allNamesLower = sessionNames.map((n) => String(n).toLowerCase());
                                const durationStr = String(rec.duration || rec.leave_duration || rec.type || "").toUpperCase();

                                const hasS1 =
                                  allNamesLower.some((n) => n.includes("session1") || n.includes("session 1") || n.includes("morning") || n.includes("first") || n === "1") ||
                                  sessionIds.some(
                                    (id) =>
                                      id === "session_morning" ||
                                      id === "session1" ||
                                      id === "1" ||
                                      (morningSession && (id === String(morningSession._id).toLowerCase() || id === String(morningSession.id).toLowerCase()))
                                  );

                                const hasS2 =
                                  allNamesLower.some((n) => n.includes("session2") || n.includes("session 2") || n.includes("afternoon") || n.includes("second") || n === "2") ||
                                  sessionIds.some(
                                    (id) =>
                                      id === "session_afternoon" ||
                                      id === "session2" ||
                                      id === "2" ||
                                      (afternoonSession && (id === String(afternoonSession._id).toLowerCase() || id === String(afternoonSession.id).toLowerCase()))
                                  );

                                if (hasS1 && hasS2) {
                                  return { periodLabel: "Full Day", badgeColor: "bg-indigo-50 text-indigo-700 border-indigo-200" };
                                }

                                if (hasS1) {
                                  return { periodLabel: "First Half", badgeColor: "bg-sky-50 text-sky-700 border-sky-200" };
                                }

                                if (hasS2) {
                                  return { periodLabel: "Second Half", badgeColor: "bg-purple-50 text-purple-700 border-purple-200" };
                                }

                                for (const sid of sessionIds) {
                                  if (sid && Array.isArray(sessions) && sessions.length > 0) {
                                    const matchedSess = sessions.find((s) => String(s._id || s.id).toLowerCase() === sid);
                                    if (matchedSess) {
                                      const mName = String(matchedSess.session_name || matchedSess.name || "").toLowerCase();
                                      if (mName.includes("morning") || mName.includes("first") || mName.includes("1") || mName.includes("session1")) {
                                        return { periodLabel: "First Half", badgeColor: "bg-sky-50 text-sky-700 border-sky-200" };
                                      }
                                      if (mName.includes("afternoon") || mName.includes("second") || mName.includes("2") || mName.includes("session2")) {
                                        return { periodLabel: "Second Half", badgeColor: "bg-purple-50 text-purple-700 border-purple-200" };
                                      }
                                    }
                                  }
                                }

                                if (durationStr === "FULL_DAY" || allNamesLower.some((n) => n.includes("full"))) {
                                  return { periodLabel: "Full Day", badgeColor: "bg-indigo-50 text-indigo-700 border-indigo-200" };
                                }

                                if (durationStr === "SESSION" || durationStr === "HALF_DAY" || durationStr === "PARTIAL" || sessionNames.length > 0 || sessionIds.length > 0) {
                                  return { periodLabel: "Half Day", badgeColor: "bg-amber-50 text-amber-700 border-amber-200" };
                                }

                                return { periodLabel: "Full Day", badgeColor: "bg-indigo-50 text-indigo-700 border-indigo-200" };
                              };

                              const { periodLabel, badgeColor } = getSessionInfo(item);

                              return (
                                <div className="flex flex-col items-end gap-1">
                                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${badgeColor}`}>
                                    {periodLabel}
                                  </span>
                                </div>
                              );
                            })()
                          )}
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHeader;
